import { useI18n } from "@/lib/i18n/i18n_provider";
import { Option } from "react-select/src/filters";
import {
  GalleryFilterConfig,
  PartnerOfferingLocation,
} from "../../data/partner_offering";
import { uniqueBy } from "../../lib/js_utils/array_utils";
import { DEFAULT_CAPACITY_OPTIONS } from "../../offering/template_offering_listing_base";
import { MeetingRoomsFilter } from "../../offering/template_offering_listing_meeting_room";
import { MobileFilter } from "../components/filters/mobile_filters";
import { EventLayout } from "./use_get_partner_event_layouts";
import { useCapacityFilterFeatureFlag } from "@/core/splitio";
import { ProductCategoryOption } from "../components/filters/product_category_options";

const MIN_ROOMS_FOR_CAPACITY_FILTER = 3;
const MIN_LOCATIONS_FOR_LOCATION_FILTER = 1;

export function useFilters(
  filterConfig: GalleryFilterConfig,
  cities: Option[],
  states: Option[],
  locations: PartnerOfferingLocation[],
  eventLayouts: EventLayout[],
  filterValue: MeetingRoomsFilter,
  setFilter: (filter: MeetingRoomsFilter) => void,
  productCategoryOptions: ProductCategoryOption[]
) {
  const { fbt } = useI18n();
  const capacityFilterFeatureFlag = useCapacityFilterFeatureFlag();
  const filters: MobileFilter = {};
  let numberOfFilters = 0;

  if (filterConfig.state) {
    filters.state = {
      label: fbt("State"),
      iconName: "state",
      options: states,
      value: filterValue.state,
      onChange: (state) => {
        setFilter({
          ...filterValue,
          state,
          city: undefined,
          locationID: undefined,
        });
      },
    };
    numberOfFilters++;
  }

  if (filterConfig.city) {
    filters.city = {
      label: fbt("City"),
      iconName: "city",
      options: cities,
      value: filterValue.city,
      onChange: (city) => {
        setFilter({
          ...filterValue,
          city,
          locationID:
            city !== filterValue.city ? undefined : filterValue.locationID,
        });
      },
    };
    numberOfFilters++;
  }

  if (filterConfig.location) {
    const options = locations.map((l) => ({
      label: l.locationDisplay,
      value: l.location.id,
      data: { city: l.location.city, state: l.location.state },
    }));
    const uniqueOptions = uniqueBy(options, (o) => o.value);
    if (uniqueOptions.length > MIN_LOCATIONS_FOR_LOCATION_FILTER) {
      filters.location = {
        label: fbt("Location"),
        iconName: "location",
        options: uniqueOptions,
        value: uniqueOptions.some((opt) => opt.value === filterValue.locationID)
          ? filterValue.locationID
          : "",
        onChange: (locationID) => setFilter({ ...filterValue, locationID }),
      };
      numberOfFilters++;
    }
  }

  if (filterConfig.layout) {
    const options = eventLayouts;
    filters.layout = {
      label: fbt("Layout"),
      iconName: "layout",
      options: options.map((o) => ({ label: o.name, value: o.id, data: o })),
      onChange: (layoutId) => setFilter({ ...filterValue, layoutId }),
      value: filterValue.layoutId,
    };
    numberOfFilters++;
  }

  if (
    filterConfig.capacity &&
    locations.length > MIN_ROOMS_FOR_CAPACITY_FILTER
  ) {
    const options = (
      filterConfig.capacityOptions || DEFAULT_CAPACITY_OPTIONS
    ).map((o) => ({
      label: o.label,
      value: `${o.value.minCapacity}`,
      data: {
        min: o.value.minCapacity,
        max: o.value.maxCapacity,
      },
    }));
    filters.capacity = {
      label: fbt("Capacity"),
      iconName: "capacity",
      options,
      value: filterValue.minCapacity?.toString(),
      onChange: (minCapacity) =>
        setFilter({
          ...filterValue,
          minCapacity: Number(minCapacity) || undefined,
          maxCapacity: capacityFilterFeatureFlag
            ? undefined
            : options.find((o) => o.value === minCapacity)?.data.max ||
              undefined,
        }),
    };
    numberOfFilters++;
  }

  if (filterConfig.datetime) {
    filters.dateTime = {
      label: fbt("When"),
      iconName: "calendar",
      options: [],
      onChange: () => {},
    };
    numberOfFilters++;
  }

  if (filterConfig.productCategory) {
    filters.productCategory = {
      label: fbt("Category"),
      iconName: "product-category",
      options: productCategoryOptions.map((o) => ({
        label: fbt(o.label),
        value: o.value,
        data: o.data,
      })),
      onChange: (productCategory?: string) =>
        setFilter({
          ...filterValue,
          productCategory: productCategory,
        }),
      value: filterValue.productCategory,
    };
    numberOfFilters++;
  }

  return { filters, numberOfFilters };
}
